import { useState } from 'react';
import { isAxiosError } from 'axios';
import { apiClient } from '@config/api';
import { limitFormSchemaType } from '../LimitForm.schema';

interface AlertMessage {
  title: string;
  description: string;
  variant: 'error' | 'success';
}

interface Status {
  loading: boolean;
  alertMessage: AlertMessage;
  success: boolean;
  error: boolean;
}

type MessageType = {
  [key: string]: AlertMessage;
};

/**
 * Edit limit state hook
 *
 * @remarks
 * this hook handles the screen status relationated to the service endpoint and response
 * @param reset - Function to reset form values
 */
const usePatchLimitDetail = (reset: (v: object) => void, limitId?: string) => {
  const MESSAGES: MessageType = {
    BAD_REQUEST: {
      title: 'COMMON:ERRORS:GENERIC_ERROR_TITLE',
      description: 'LIMITS:ERRORS:BAD_REQUEST_DESCRIPTION',
      variant: 'error',
    },
    ERROR_GENERIC: {
      title: 'COMMON:ERRORS:GENERIC_ERROR_TITLE',
      description: 'COMMON:ERRORS:IT_ERROR',
      variant: 'error',
    },
    SUCCESS: {
      title: 'LIMITS:MODAL:SUCCESS',
      description: '',
      variant: 'success',
    },
  } as const;

  const [status, setStatus] = useState<Status>({
    loading: false,
    success: false,
    error: false,
    alertMessage: MESSAGES.ERROR_GENERIC,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  /**
   * Function to set the error state.
   * @param {boolean} error - The error state.
   */
  const setIsError = (error: boolean) => {
    setStatus((prevState) => ({ ...prevState, error }));
  };

  /**
   * Function to set the sucess state.
   * @param {boolean} success - The success state.
   */
  const setIsSuccess = (success: boolean) => {
    setStatus((prevState) => ({ ...prevState, success }));
  };

  /**
   * Service for editing a limit
   * @param body - The request body.
   */
  async function patchLimitDetail(body: Partial<limitFormSchemaType>, limitId: string) {
    const url = `/v3/partner/limits/details/${limitId}`;

    const response = await apiClient.patch(url, body);
    return response.data;
  }
  /**
   *  Map dirty fields and fill them with the values
   * @param dirtyFields
   * @param allValues
   * @returns
   */
  function dirtyValues(
    dirtyFields: Partial<limitFormSchemaType>,
    allValues: limitFormSchemaType
  ): Partial<limitFormSchemaType> {
    return Object.fromEntries(
      Object.keys(dirtyFields).map((key) => [key, allValues[key as keyof limitFormSchemaType]])
    );
  }
  /**
   * Edit limit service handler
   * @async
   * @param body - request payload
   */
  async function submit(body: limitFormSchemaType, dirtyFields: object) {
    try {
      setStatus((prevState) => ({ ...prevState, loading: true }));
      if (!limitId) {
        throw 'No limit id';
      }
      await patchLimitDetail(dirtyValues(dirtyFields, body), limitId);
      setStatus({
        loading: false,
        error: false,
        success: true,
        alertMessage: MESSAGES.SUCCESS,
      });
      reset(body);
    } catch (error) {
      let alertMessage: AlertMessage;
      if (isAxiosError(error) && error?.response?.status === 400) {
        alertMessage = MESSAGES.BAD_REQUEST;
      } else {
        alertMessage = MESSAGES.ERROR_GENERIC;
      }
      setStatus({ error: true, success: false, loading: false, alertMessage });
    } finally {
      setIsModalOpen(false);
    }
  }

  const { loading: isLoading, success: isSuccess, error: isError, alertMessage } = status;

  return {
    isLoading,
    isSuccess,
    isError,
    alertMessage,
    setIsSuccess,
    setIsError,
    submit,
    isModalOpen,
    setIsModalOpen,
  };
};

export default usePatchLimitDetail;
